import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Brand from "@/components/Brand";
import {
  Dumbbell, Library, CalendarDays, LineChart, DollarSign, UserCircle,
  ArrowRight, Check, BrainCircuit, Sparkles, ShieldCheck,
} from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1551847677-dc82d764e1eb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwxfHxjYWxtJTIwdGhlcmFweSUyMHNlc3Npb258ZW58MHx8fHwxNzg2NjU2MTY4fDA&ixlib=rb-4.1.0&q=85";

const benefits = [
  "Exercícios entre sessões", "Conteúdos personalizados", "Acompanhamento das atividades",
  "Agenda integrada", "Agendamento online", "Valores configuráveis",
  "Área exclusiva do cliente", "Mais organização", "Maior continuidade no acompanhamento",
];

const features = [
  { icon: Dumbbell, title: "Exercícios personalizados", desc: "Recomende práticas específicas para cada cliente com áudio, vídeo, PDF e prazos." },
  { icon: Library, title: "Biblioteca de conteúdos", desc: "Áudios, vídeos e materiais organizados por categoria, com conteúdos premium." },
  { icon: CalendarDays, title: "Agenda e agendamento", desc: "Configure tipos de consulta, valores e receba solicitações de agendamento." },
  { icon: LineChart, title: "Acompanhamento de atividades", desc: "Veja o progresso de cada cliente entre uma sessão e outra." },
  { icon: DollarSign, title: "Você define seus valores", desc: "Configure o preço de cada tipo de atendimento. Total autonomia." },
  { icon: UserCircle, title: "Área exclusiva do cliente", desc: "Uma experiência acolhedora onde o cliente vê sua jornada e evolução." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-background">
      {/* nav */}
      <header className="fixed top-0 inset-x-0 z-50 bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl border-b border-slate-200/50 dark:border-slate-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Brand />
          <div className="flex items-center gap-2">
            <Link to="/login"><Button variant="ghost" data-testid="nav-login-btn">Entrar</Button></Link>
            <Link to="/cadastro-cliente"><Button variant="outline" className="rounded-full" data-testid="nav-client-btn">Sou Cliente</Button></Link>
            <Link to="/cadastro">
              <Button className="rounded-full" data-testid="nav-register-btn">Sou Hipnoterapeuta</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* hero */}
      <section className="relative overflow-hidden pt-32 pb-20 lg:pt-40 lg:pb-28">
        <div className="absolute -top-20 -left-20 w-[500px] h-[500px] bg-primary/20 rounded-full blur-3xl" aria-hidden="true" />
        <div className="absolute top-40 -right-20 w-[400px] h-[400px] bg-accent/20 rounded-full blur-3xl" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div className="animate-fade-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-6">
              <Sparkles className="w-3.5 h-3.5" /> Para hipnoterapeutas e seus clientes
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05] text-slate-900 dark:text-white">
              Uma experiência completa de acompanhamento para hipnoterapeutas e seus clientes, <span className="text-primary">além da sessão.</span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-xl">
              Exercícios, conteúdos personalizados e agendamento em um só lugar — conectando profissional
              e cliente para uma evolução que não para quando a sessão acaba.
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <Link to="/cadastro">
                <Button size="lg" className="rounded-full text-base h-12 px-8" data-testid="hero-cta-btn">
                  QUERO MINHA PLATAFORMA <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="rounded-full text-base h-12 px-8" data-testid="hero-login-btn">
                  Já tenho conta
                </Button>
              </Link>
            </div>
            <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
              <ShieldCheck className="w-4 h-4 text-accent" /> Plataforma preparada para a LGPD
            </div>
          </div>
          <div className="relative animate-fade-up" style={{ animationDelay: "0.15s" }}>
            <div className="relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-slate-200 dark:ring-slate-800">
              <img src={HERO_IMG} alt="Sessão de hipnoterapia acolhedora" className="w-full h-[420px] object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 to-transparent" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-card rounded-2xl border border-border p-4 shadow-xl w-52 hidden sm:block">
              <div className="flex items-center gap-2 text-accent text-sm font-medium">
                <BrainCircuit className="w-4 h-4" /> Progresso do cliente
              </div>
              <div className="mt-2 h-2 rounded-full bg-secondary overflow-hidden">
                <div className="h-full w-[72%] bg-accent rounded-full" />
              </div>
              <p className="mt-2 text-xs text-muted-foreground">72% das atividades concluídas</p>
            </div>
          </div>
        </div>
      </section>

      {/* flow ribbon */}
      <section className="border-y border-border bg-card py-5 overflow-hidden">
        <div className="flex items-center justify-center gap-4 sm:gap-8 text-sm font-medium text-muted-foreground flex-wrap px-4">
          {["Consulta", "Exercícios", "Conteúdos", "Acompanhamento", "Agendamento", "Relacionamento"].map((s, i) => (
            <span key={s} className="flex items-center gap-4 sm:gap-8">
              <span className="text-foreground">{s}</span>
              {i < 5 && <span className="text-primary">+</span>}
            </span>
          ))}
        </div>
      </section>

      {/* features */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-primary mb-3">Tudo em um só lugar</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight text-slate-900 dark:text-white">
              Uma extensão digital do seu atendimento
            </h2>
            <p className="mt-4 text-muted-foreground text-lg">
              Transforme o período entre as sessões em uma experiência estruturada de acompanhamento.
            </p>
          </div>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f, i) => (
              <div key={f.title}
                className="group rounded-2xl border border-border bg-card p-8 hover:-translate-y-1 hover:shadow-lg hover:border-primary/30 transition-all duration-200 animate-fade-up"
                style={{ animationDelay: `${i * 0.06}s` }}>
                <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center mb-5 group-hover:bg-primary group-hover:text-white text-primary transition-colors">
                  <f.icon className="w-6 h-6" />
                </div>
                <h3 className="font-heading text-xl font-medium mb-2">{f.title}</h3>
                <p className="text-muted-foreground">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* benefits */}
      <section className="py-20 bg-secondary/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight text-slate-900 dark:text-white">
              Por que os hipnoterapeutas escolhem a Teraviva
            </h2>
            <p className="mt-4 text-muted-foreground text-lg">
              Aumente a percepção de valor do seu serviço e melhore a experiência dos seus clientes.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {benefits.map((b) => (
              <div key={b} className="flex items-center gap-3 bg-card rounded-xl border border-border p-4">
                <div className="w-6 h-6 rounded-full bg-accent/15 flex items-center justify-center shrink-0">
                  <Check className="w-3.5 h-3.5 text-accent" />
                </div>
                <span className="text-sm font-medium">{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900 dark:text-white">
            Não deixe o acompanhamento terminar quando a sessão acaba.
          </h2>
          <Link to="/cadastro">
            <Button size="lg" className="mt-8 rounded-full text-base h-12 px-10" data-testid="cta-bottom-btn">
              QUERO MINHA PLATAFORMA <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <footer className="border-t border-border py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Brand />
          <p className="text-xs text-muted-foreground max-w-xl text-center sm:text-right">
            Os conteúdos e exercícios desta plataforma possuem caráter educativo e complementar e não substituem
            avaliação, diagnóstico ou acompanhamento profissional.
          </p>
        </div>
      </footer>
    </div>
  );
}
